import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi, analyticsApi } from '../utils/api';
import {
    Search,
    Share2,
    Megaphone,
    Mail,
    Wallet,
    BarChart3,
    ArrowRight,
    Globe,
    TrendingUp,
    Users,
    DollarSign,
    ToggleLeft,
    ToggleRight,
    Settings
} from 'lucide-react';

const moduleIcons: Record<string, any> = {
    seo: Search,
    social: Share2,
    ads: Megaphone,
    email: Mail,
    payments: Wallet,
    analytics: BarChart3,
};

const moduleColors: Record<string, string> = {
    seo: 'bg-blue-500',
    social: 'bg-pink-500',
    ads: 'bg-orange-500',
    email: 'bg-green-500',
    payments: 'bg-purple-500',
    analytics: 'bg-cyan-500',
};

export default function ProjectDetail() {
    const { projectId } = useParams();
    const queryClient = useQueryClient();

    const { data: projectData, isLoading } = useQuery({
        queryKey: ['project', projectId],
        queryFn: () => projectsApi.get(projectId!),
        enabled: !!projectId,
    });

    const { data: dashboardData } = useQuery({
        queryKey: ['dashboard', projectId],
        queryFn: () => analyticsApi.getDashboard(projectId!),
        enabled: !!projectId,
    });

    const toggleMutation = useMutation({
        mutationFn: (moduleType: string) =>
            projectsApi.toggleModule(projectId!, moduleType),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
        },
    });

    if (isLoading) {
        return (
            <div className="animate-pulse space-y-6">
                <div className="h-12 bg-dark-800 rounded w-1/3" />
                <div className="h-48 bg-dark-800 rounded" />
            </div>
        );
    }

    const project = projectData?.data?.project;
    const modules = projectData?.data?.modules || [];
    const dashboard = dashboardData?.data?.dashboard;

    if (!project) {
        return <div>Project not found</div>;
    }

    const stats = [
        { label: 'Traffic', value: dashboard?.overview?.totalTraffic || 0, icon: TrendingUp },
        { label: 'Conversions', value: dashboard?.overview?.totalConversions || 0, icon: Users },
        { label: 'Revenue', value: `$${dashboard?.overview?.totalRevenue || 0}`, icon: DollarSign },
    ];

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-3xl font-bold">{project.name}</h1>
                    {project.websiteDomain && (
                        <div className="flex items-center gap-2 text-dark-400 mt-2">
                            <Globe className="w-4 h-4" />
                            <a
                                href={project.websiteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-primary-400"
                            >
                                {project.websiteDomain}
                            </a>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <Link
                        to={`/projects/${projectId}/settings`}
                        className="btn btn-secondary flex items-center gap-2"
                    >
                        <Settings className="w-4 h-4" />
                        Settings
                    </Link>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${project.status === 'active'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-dark-600 text-dark-400'
                        }`}>
                        {project.status}
                    </span>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {stats.map((stat) => (
                    <div key={stat.label} className="card">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-primary-600/20 rounded-xl flex items-center justify-center">
                                <stat.icon className="w-6 h-6 text-primary-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stat.value}</p>
                                <p className="text-dark-400 text-sm">{stat.label}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modules Grid */}
            <div>
                <h2 className="text-xl font-semibold mb-4">Modules</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {modules.map((module: any) => {
                        const Icon = moduleIcons[module.type] || BarChart3;
                        const bgColor = moduleColors[module.type] || 'bg-gray-500';

                        return (
                            <div key={module.id} className="card">
                                <div className="flex items-start justify-between mb-4">
                                    <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
                                        <Icon className="w-6 h-6 text-white" />
                                    </div>
                                    <button
                                        onClick={() => toggleMutation.mutate(module.type)}
                                        disabled={toggleMutation.isPending}
                                        className="text-dark-400 hover:text-white"
                                    >
                                        {module.enabled ? (
                                            <ToggleRight className="w-8 h-8 text-primary-500" />
                                        ) : (
                                            <ToggleLeft className="w-8 h-8" />
                                        )}
                                    </button>
                                </div>

                                <h3 className="font-semibold text-lg capitalize mb-1">
                                    {module.type}
                                </h3>
                                <p className="text-dark-400 text-sm mb-4">
                                    {module.enabled ? 'Module is active' : 'Module is disabled'}
                                </p>

                                <Link
                                    to={`/projects/${projectId}/${module.type}`}
                                    className="flex items-center gap-2 text-primary-400 hover:text-primary-300 text-sm"
                                >
                                    Open module <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
